import type { Dispatch, SetStateAction } from "react";
import Button from "../../../shared/components/Button";
import EmptyState from "../../../shared/components/EmptyState";
import Spinner from "../../../shared/components/Spinner";
import { formatDateTimeLabel } from "../../../shared/utils/dateTime";
import type {
  AdminTournamentBracket,
  AdminTournamentBracketMatch,
  AdminTournamentScheduleSlot,
} from "../services/eventRegistrationAdminService";
import {
  formatSets,
  isByeMatch,
  matchCodeForRound,
  matchStatusLabel,
  matchWinnerLabel,
  slotCourtSelection,
  type TournamentBracketBlock,
} from "../utils/tournamentOperations";

type ManualGroupAssignments = Record<string, string>;

type ManualPair = {
  registrationId: number;
  label: string;
};

type ManualGroupOption = {
  id: string;
  name: string;
};

function MatchSide({
  avatarUrls,
  badge,
  className = "",
  label,
  showAvatars = false,
}: {
  avatarUrls: Array<string | null | undefined>;
  badge?: string;
  className?: string;
  label: string;
  showAvatars?: boolean;
}) {
  const safeAvatarUrls = avatarUrls
    .map((value) => String(value || "").trim())
    .slice(0, 2);
  const playerNames = String(label || "")
    .split(" / ")
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 2);
  const avatarItems = playerNames.map((name, index) => {
    const initials = name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((chunk) => chunk[0]?.toUpperCase() || "")
      .join("");
    return {
      avatarUrl: safeAvatarUrls[index] || "",
      initials: initials || "TN",
    };
  });
  const shouldShowAvatars =
    showAvatars || safeAvatarUrls.some(Boolean);

  return (
    <span className={`admin-tournament-match-side ${className}`.trim()}>
      {shouldShowAvatars && avatarItems.length > 0 && (
        <span className="admin-tournament-match-side-avatars" aria-hidden="true">
          {avatarItems.map((avatar, index) => (
            <span
              key={`${label}-${index}`}
              className="admin-tournament-match-side-avatar"
            >
              {avatar.avatarUrl ? (
                <img src={avatar.avatarUrl} alt="" loading="lazy" />
              ) : (
                avatar.initials
              )}
            </span>
          ))}
        </span>
      )}
      <span>{label}</span>
      {badge && <small className="admin-tournament-seed-badge">{badge}</small>}
    </span>
  );
}

type AdminTournamentBracketPanelProps = {
  bracket: AdminTournamentBracket | null;
  canEditMatchup: (match: AdminTournamentBracketMatch) => boolean;
  canGenerateStructure?: boolean;
  categoryKey: string;
  groupBracketBlocks: TournamentBracketBlock[];
  loadingBracket: boolean;
  manualAssignments: ManualGroupAssignments;
  manualGroupCount: string;
  manualGroupOptions: ManualGroupOption[];
  manualPairs: ManualPair[];
  onGenerateAutomatic: () => void;
  onGenerateManual: () => void;
  onGenerateTimeline: () => void;
  onLoadManualPairs: () => void;
  onOpenMatchupModal: (match: AdminTournamentBracketMatch) => void;
  onPublish: () => void;
  roundBracketBlocks: TournamentBracketBlock[];
  saving: boolean;
  scheduledByMatchId: Map<number, AdminTournamentScheduleSlot>;
  setManualAssignments: Dispatch<SetStateAction<ManualGroupAssignments>>;
  setManualGroupCount: Dispatch<SetStateAction<string>>;
  tournamentConfigurationBlocker: string;
};

function AdminTournamentBracketBlock({
  block,
  canEditMatchup,
  onOpenMatchupModal,
  scheduledByMatchId,
}: {
  block: TournamentBracketBlock;
  canEditMatchup: (match: AdminTournamentBracketMatch) => boolean;
  onOpenMatchupModal: (match: AdminTournamentBracketMatch) => void;
  scheduledByMatchId: Map<number, AdminTournamentScheduleSlot>;
}) {
  const visibleMatches = block.matches.filter((match) => !isByeMatch(match));
  const showTeamList = block.teams.length > 2 || visibleMatches.length === 0;
  return (
    <section
      className={`admin-tournament-bracket-block${
        block.kind === "group" ? " is-group" : ""
      }${block.kind === "group" && !showTeamList ? " is-compact-group" : ""}`}
    >
      <div className="admin-tournament-bracket-block-head">
        <div>
          <h5>{block.title}</h5>
          <span>{block.subtitle}</span>
        </div>
        <strong>{visibleMatches.length} jogos</strong>
      </div>

      {showTeamList && (
        <div className="admin-tournament-bracket-teams">
          {block.teams.map((team) => (
            <span key={`${block.id}-${team.registrationId || team.label}`}>
              <span>{team.label}</span>
            </span>
          ))}
        </div>
      )}

      <div className="admin-tournament-bracket-match-list">
        {visibleMatches.length === 0 ? (
          <EmptyState
            className="admin-empty-state"
            icon={<span className="material-symbols-outlined">sports_tennis</span>}
            title="Nenhum confronto gerado para esta chave."
          />
        ) : (
          visibleMatches.map((match) => {
            const scheduled = scheduledByMatchId.get(match.id);
            const courtName = scheduled ? slotCourtSelection(scheduled) : "";
            return (
              <article
                key={match.id}
                className="admin-tournament-bracket-match"
              >
                <div className="admin-tournament-match-head">
                  <strong>
                    {block.kind === "group"
                      ? `Jogo ${match.matchNumber}`
                      : matchCodeForRound(
                          match.roundCode,
                          match.matchNumber,
                        )}
                  </strong>
                  <span>{matchStatusLabel(match.status)}</span>
                </div>
                <div className="admin-tournament-match-sides">
                  <MatchSide
                    avatarUrls={[
                      match.sideAPlayerAvatarUrl,
                      match.sideAPartnerAvatarUrl,
                    ]}
                    className={match.winnerSide === "A" ? "is-winner" : ""}
                    label={match.sideALabel || "A definir"}
                    showAvatars={Number(match.sideARegistrationId || 0) > 0}
                  />
                  <small>{formatSets(match)}</small>
                  <MatchSide
                    avatarUrls={[
                      match.sideBPlayerAvatarUrl,
                      match.sideBPartnerAvatarUrl,
                    ]}
                    className={match.winnerSide === "B" ? "is-winner" : ""}
                    label={match.sideBLabel || "A definir"}
                    showAvatars={Number(match.sideBRegistrationId || 0) > 0}
                  />
                </div>
                {matchWinnerLabel(match) && (
                  <p className="admin-tournament-match-meta">
                    Vencedor: {matchWinnerLabel(match)}
                  </p>
                )}
                <p className="admin-tournament-bracket-schedule">
                  <span>
                    {scheduled
                      ? formatDateTimeLabel(scheduled.slotStartAt)
                      : "Horário a definir"}
                  </span>
                  <strong>{courtName || "Quadra a definir"}</strong>
                </p>
                {canEditMatchup(match) && (
                  <div className="admin-tournament-match-actions">
                    <Button
                      type="button"
                      variant="pill"
                      onClick={() => onOpenMatchupModal(match)}
                    >
                      Editar confronto
                    </Button>
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}

export default function AdminTournamentBracketPanel({
  bracket,
  canEditMatchup,
  canGenerateStructure = true,
  categoryKey,
  groupBracketBlocks,
  loadingBracket,
  manualAssignments,
  manualGroupCount,
  manualGroupOptions,
  manualPairs,
  onGenerateAutomatic,
  onGenerateManual,
  onGenerateTimeline,
  onLoadManualPairs,
  onOpenMatchupModal,
  onPublish,
  roundBracketBlocks,
  saving,
  scheduledByMatchId,
  setManualAssignments,
  setManualGroupCount,
  tournamentConfigurationBlocker,
}: AdminTournamentBracketPanelProps) {
  const isPublished =
    String(bracket?.status || "").toLowerCase() === "published" &&
    Boolean(String(bracket?.publishedAt || "").trim());

  const renderBracketBlock = (block: TournamentBracketBlock) => (
    block.kind !== "group" &&
    block.teams.length === 0 &&
    block.matches.every((match) => isByeMatch(match)) ? null : (
    <AdminTournamentBracketBlock
      key={block.id}
      block={block}
      canEditMatchup={canEditMatchup}
      onOpenMatchupModal={onOpenMatchupModal}
      scheduledByMatchId={scheduledByMatchId}
    />
    )
  );

  return (
    <div className="admin-tournament-ops-panel">
      <div className="admin-tournament-ops-actions">
        <Button
          type="button"
          variant="primary"
          disabled={saving || !categoryKey || !canGenerateStructure}
          onClick={onGenerateAutomatic}
        >
          Gerar chaves
        </Button>
        <Button
          type="button"
          variant="pill"
          disabled={saving || !canGenerateStructure || !bracket}
          onClick={onGenerateTimeline}
        >
          Gerar timeline geral
        </Button>
        <Button
          type="button"
          variant="pill"
          disabled={saving || !categoryKey}
          onClick={onLoadManualPairs}
        >
          Atualizar duplas
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={saving || !categoryKey || !bracket || isPublished}
          onClick={onPublish}
        >
          Publicar chaves
        </Button>
      </div>
      {bracket && (
        <div
          className={`admin-tournament-bracket-publication${
            isPublished ? " is-published" : " is-draft"
          }`}
          role="status"
          aria-live="polite"
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            {isPublished ? "visibility" : "visibility_off"}
          </span>
          <div>
            <strong>{isPublished ? "Chaves publicadas" : "Chaves em rascunho"}</strong>
            <span>
              {isPublished
                ? "Os atletas já podem visualizar este chaveamento."
                : "Revise os confrontos e publique quando estiver pronto."}
            </span>
          </div>
        </div>
      )}
      {tournamentConfigurationBlocker && (
        <p className="status-message">{tournamentConfigurationBlocker}</p>
      )}
      <section className="admin-tournament-manual-groups">
        <div className="admin-tournament-manual-head">
          <div>
            <h5>Montagem manual</h5>
            <p>Escolha em qual grupo cada dupla confirmada deve entrar.</p>
          </div>
          <label className="field">
            <span>Grupos</span>
            <input
              type="number"
              min={1}
              max={16}
              value={manualGroupCount}
              onChange={(event) => setManualGroupCount(event.target.value)}
            />
          </label>
        </div>
        {manualPairs.length === 0 ? (
          <EmptyState
            className="admin-empty-state"
            icon={<span className="material-symbols-outlined">groups</span>}
            title="Nenhuma dupla confirmada nesta categoria."
          />
        ) : (
          <>
            <div className="admin-tournament-manual-pairs">
              {manualPairs.map((pair) => (
                <label
                  key={pair.registrationId}
                  className="admin-tournament-manual-pair"
                >
                  <span>{pair.label}</span>
                  <select
                    value={
                      manualAssignments[String(pair.registrationId)] ||
                      manualGroupOptions[0]?.id ||
                      "a"
                    }
                    onChange={(event) =>
                      setManualAssignments((current) => ({
                        ...current,
                        [String(pair.registrationId)]: event.target.value,
                      }))
                    }
                  >
                    {manualGroupOptions.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <div className="admin-tournament-manual-summary">
              {manualGroupOptions.map((group) => {
                const count = manualPairs.filter(
                  (pair) =>
                    manualAssignments[String(pair.registrationId)] === group.id,
                ).length;
                return (
                  <span key={group.id}>
                    {group.name}: {count}
                  </span>
                );
              })}
            </div>
            <Button
              type="button"
              variant="primary"
              disabled={saving || !categoryKey || !canGenerateStructure}
              onClick={onGenerateManual}
            >
              Salvar alterações das chaves
            </Button>
          </>
        )}
      </section>
      {loadingBracket ? (
        <Spinner label="Carregando chave..." />
      ) : !bracket ? (
        <EmptyState
          className="admin-empty-state"
          icon={<span className="material-symbols-outlined">account_tree</span>}
          title="Nenhuma chave gerada para esta categoria."
        />
      ) : (
        <div className="admin-tournament-bracket-stack">
          {groupBracketBlocks.length > 0 && (
            <div className="admin-tournament-bracket-group-grid">
              {groupBracketBlocks.map(renderBracketBlock)}
            </div>
          )}
          {roundBracketBlocks.length > 0 && (
            <div className="admin-tournament-bracket-round-stack">
              {roundBracketBlocks.map(renderBracketBlock)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
